import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useState } from 'react'

import { ListSectionHeader, Text } from '@lmc/ui-kit'
import { ConsentFormTemplate } from '@lmc/common/types'
import { find } from 'lodash'
import { getResidentShortCut } from '../utils'
import TextEditor from './TextEditor'
import { colors } from '@lmc/common/constants'
import { Resident } from '@lmc/ts-common'
import { CarePlanSelector } from 'components/generic'
import styled from 'styled-components'
import { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import useFeatureFlags from 'hooks/useFeatureFlags'

type ModalConsentFormsProps = {
  copyOfTemplates: ConsentFormTemplate[]
  customForms: ConsentFormTemplate[]
  handleCancelEditForm: (id: string) => void
  handleEditForm: (id: string) => void
  resident: Resident
  setCopyOfTemplates: Dispatch<SetStateAction<any[]>>
  setCustomForms: Dispatch<SetStateAction<any[]>>
  sideControlIds: {
    isSelected: boolean
    title: string
    id: string
  }[]
  templates: any[]
  translate: (key: string, variable?: any) => string
  onCarePlanChange?: (state: Record<string, Array<CarePlanAdlExtended>>) => void
}

export const ModalConsentForms = ({
  copyOfTemplates,
  handleCancelEditForm,
  handleEditForm,
  resident,
  setCopyOfTemplates,
  sideControlIds,
  templates,
  translate,
  customForms,
  setCustomForms,
  onCarePlanChange
}: ModalConsentFormsProps): ReactElement => {
  const featureFlags = useFeatureFlags({ flagNames: ['informed_consent'] }).data
  const isInformedConsentEnabled =
    featureFlags['informed_consent']?.enabled || false

  const [selectedCarePlans, setSelectedCarePlans] =
    useState<Record<string, Array<CarePlanAdlExtended>>>()
  const handleSelectedCarePlans = (
    carePlans: Array<CarePlanAdlExtended>,
    recordId: string
  ) => {
    setSelectedCarePlans((prevState) => {
      const newState = {
        ...prevState,
        [recordId]: carePlans
      }
      if (onCarePlanChange) {
        onCarePlanChange(newState)
      }
      return newState
    })
  }
  return (
    <>
      <ListSectionHeader
        title={`${resident?.preferredName || resident?.name?.first}${translate(
          'consentsTo'
        )}`}
        shortCut={getResidentShortCut(resident)}
        titleIcon={undefined}
      />
      {customForms?.map((item) => {
        const { isCreated } = item
        return (
          <TextEditor
            key={item.id}
            isSelected={isCreated || false}
            item={item}
            resident={resident}
            setCopyOfTemplates={setCustomForms}
            copyOfTemplates={customForms}
            handleCancelEditForm={handleCancelEditForm}
            handleEditForm={handleEditForm}
            customEntry
          />
        )
      })}

      {templates?.map((item) => {
        const isSelected = find(sideControlIds, {
          isSelected: true,
          id: item.id
        })

        return (
          !!isSelected && (
            <Wrapper>
              <TextEditor
                key={item.id}
                isSelected={!!isSelected}
                item={item}
                resident={resident}
                handleCancelEditForm={handleCancelEditForm}
                handleEditForm={handleEditForm}
                setCopyOfTemplates={setCopyOfTemplates}
                copyOfTemplates={copyOfTemplates}
              />
              {isInformedConsentEnabled && (
                <CarePlanSelector
                  residentId={resident.id}
                  onChange={(carePlans) =>
                    handleSelectedCarePlans(carePlans, item.id)
                  }
                  selectedCarePlans={selectedCarePlans?.[item.id] || []}
                />
              )}
            </Wrapper>
          )
        )
      })}
      {customForms.length === 1 &&
        !find(sideControlIds, {
          isSelected: true
        }) && (
          <Text style={styles.nothing as React.CSSProperties} as="h4">
            {translate('populateHere')}
          </Text>
        )}
    </>
  )
}

const Wrapper = styled.div`
  border-radius: 4px;
  padding: 0 9px 24px 9px;
  background: ${colors.white};
`

const styles = {
  nothing: {
    textAlign: 'center',
    marginTop: 50,
    color: colors.grey
  }
}

export default ModalConsentForms
